import React, { useState } from 'react';
import { Search, DollarSign, Check, X, Eye, FileText, Download } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { mockVerifiedPayouts, mockPropFirms, getUserById, getFirmById, getPendingPayouts } from '@/data/mock';
import { toast } from 'sonner';

const METHOD_COLORS = {
  'Bank Wire': 'bg-blue-100 text-blue-700',
  'Crypto': 'bg-purple-100 text-purple-700',
  'PayPal': 'bg-indigo-100 text-indigo-700',
  'Deel': 'bg-emerald-100 text-emerald-700',
  'Wise': 'bg-cyan-100 text-cyan-700',
  'Rise': 'bg-orange-100 text-orange-700',
};

export const VerifiedPayoutsPage = () => {
  const [payouts, setPayouts] = useState(mockVerifiedPayouts);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [firmFilter, setFirmFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [verifyPayout, setVerifyPayout] = useState(null);
  const [rejectPayout, setRejectPayout] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const pendingCount = payouts.filter(p => p.status === 'pending').length;
  const verifiedThisMonth = payouts.filter(p => {
    const thisMonth = new Date().getMonth();
    const payoutMonth = new Date(p.submittedAt).getMonth();
    return p.status === 'verified' && payoutMonth === thisMonth;
  }).length;
  const totalVerified = payouts.filter(p => p.status === 'verified').length;

  const filteredPayouts = payouts.filter(payout => {
    const matchesTab = activeTab === 'all' || payout.status === activeTab;
    const user = getUserById(payout.userId);
    const firm = getFirmById(payout.firmId);
    const matchesSearch = 
      user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      firm?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFirm = firmFilter === 'all' || payout.firmId === firmFilter;
    const matchesMethod = methodFilter === 'all' || payout.method === methodFilter;
    return matchesTab && matchesSearch && matchesFirm && matchesMethod;
  });

  const handleVerify = (payout) => {
    setPayouts(payouts.map(p => 
      p.id === payout.id ? { ...p, status: 'verified' } : p
    ));
    toast.success('Payout verified successfully');
    setVerifyPayout(null);
    setSelectedPayout(null);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setPayouts(payouts.map(p => 
      p.id === rejectPayout.id ? { ...p, status: 'rejected', notes: rejectReason } : p
    ));
    toast.success('Payout rejected');
    setRejectPayout(null);
    setRejectReason('');
    setSelectedPayout(null);
  };

  return (
    <div>
      <PageHeader
        title="Verified Payouts"
        description="Manage and verify trader payout submissions"
        breadcrumbs={['Admin', 'Community', 'Verified Payouts']}
      />

      <div className="grid gap-6 md:grid-cols-3 mb-6">
        <StatsCard
          title="Pending"
          value={pendingCount}
          icon={DollarSign}
          iconBg="bg-amber-100 text-amber-600"
        />
        <StatsCard
          title="Verified This Month"
          value={verifiedThisMonth}
          icon={Check}
          iconBg="bg-emerald-100 text-emerald-600"
        />
        <StatsCard
          title="Total Verified"
          value={totalVerified}
          icon={DollarSign}
          iconBg="bg-blue-100 text-blue-600"
        />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pending">
                  Pending
                  {pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs bg-amber-500">
                      {pendingCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="verified">Verified</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-3">
              <Select value={firmFilter} onValueChange={setFirmFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by firm" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Firms</SelectItem>
                  {mockPropFirms.slice(0, 5).map(firm => (
                    <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  {Object.keys(METHOD_COLORS).map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trader</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Firm</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Method</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayouts.map((payout) => {
                  const user = getUserById(payout.userId);
                  const firm = getFirmById(payout.firmId);
                  
                  return (
                    <TableRow key={payout.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user?.avatar} />
                            <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium text-slate-900">{user?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <img src={firm?.logo} alt="" className="h-6 w-6 rounded" />
                          <span className="text-sm text-slate-700">{firm?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-bold text-slate-900">
                          ${payout.amount.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={METHOD_COLORS[payout.method]}>
                          {payout.method}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {format(new Date(payout.payoutDate), 'MMM dd, yyyy')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={payout.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {payout.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => setVerifyPayout(payout)}
                                className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Verify
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setRejectPayout(payout)}
                                className="h-7 px-2"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedPayout(payout)}
                            className="h-7 px-2"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Sheet open={!!selectedPayout} onOpenChange={() => setSelectedPayout(null)}>
        <SheetContent className="w-[480px] overflow-y-auto">
          {selectedPayout && (
            <>
              <SheetHeader>
                <SheetTitle>Payout Details</SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">TRADER</p>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={getUserById(selectedPayout.userId)?.avatar} />
                      <AvatarFallback>{getUserById(selectedPayout.userId)?.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-slate-900">{getUserById(selectedPayout.userId)?.name}</p>
                      <p className="text-sm text-slate-600">{getUserById(selectedPayout.userId)?.email}</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <p className="text-xs text-muted-foreground mb-2">FIRM</p>
                  <div className="flex items-center gap-2">
                    <img src={getFirmById(selectedPayout.firmId)?.logo} alt="" className="h-8 w-8 rounded" />
                    <span className="font-medium">{getFirmById(selectedPayout.firmId)?.name}</span>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-3">PAYOUT DETAILS</p>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Amount</span>
                      <span className="text-lg font-bold text-slate-900">${selectedPayout.amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Method</span>
                      <Badge className={METHOD_COLORS[selectedPayout.method]}>{selectedPayout.method}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Payout Date</span>
                      <span className="text-sm font-medium">{format(new Date(selectedPayout.payoutDate), 'MMMM dd, yyyy')}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-2">PROOF DOCUMENT</p>
                  <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <img src={selectedPayout.proofUrl} alt="Proof" className="w-full rounded" />
                    <Button variant="outline" size="sm" className="w-full mt-3">
                      <Download className="h-4 w-4 mr-2" />
                      Download Proof
                    </Button>
                  </div>
                </div>

                {selectedPayout.notes && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">NOTES</p>
                    <p className="text-sm text-slate-700">{selectedPayout.notes}</p>
                  </div>
                )}

                <Separator />

                {selectedPayout.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button onClick={() => setVerifyPayout(selectedPayout)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      <Check className="h-4 w-4 mr-2" />
                      Verify
                    </Button>
                    <Button onClick={() => setRejectPayout(selectedPayout)} variant="outline" className="flex-1">
                      <X className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}

                {selectedPayout.status === 'verified' && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                    <Check className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-emerald-900">Verified and visible on public page</p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!verifyPayout}
        onOpenChange={() => setVerifyPayout(null)}
        title="Verify Payout"
        description={`Confirm verification of $${verifyPayout?.amount.toLocaleString()} payout from ${getFirmById(verifyPayout?.firmId)?.name}? This will make it publicly visible.`}
        confirmLabel="Verify Payout"
        onConfirm={() => handleVerify(verifyPayout)}
      />

      <ConfirmDialog
        open={!!rejectPayout}
        onOpenChange={() => { setRejectPayout(null); setRejectReason(''); }}
        title="Reject Payout"
        description={
          <div className="space-y-3">
            <p>Why are you rejecting this payout?</p>
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
        }
        confirmLabel="Reject Payout"
        variant="destructive"
        onConfirm={handleReject}
      />
    </div>
  );
};